import * as THREE from 'three';
import {AbstractPeriodicIntersectionSolver} from './AbstractPeriodicIntersectionSolver';

/**
 * 二维圆解析定义。
 */
export interface CircleIntersectionDefinition2D {
    /** 圆心。 */
    center: THREE.Vector2;
    /** 圆半径。 */
    radius: number;
}

/**
 * 两个二维圆的单个真实交点。
 */
export interface CircleCircleIntersection2D {
    /** 二维交点坐标。 */
    point: THREE.Vector2;
    /** 交点在第一个圆上的角参数，范围 `[0, 2π)`。 */
    firstCircleAngle: number;
    /** 交点在第二个圆上的角参数，范围 `[0, 2π)`。 */
    secondCircleAngle: number;
}

/**
 * 两个二维圆的真实交点求解器。
 *
 * 职责：
 * 1. 按圆心距离、半径和与半径差区分同心、外离、外切、双交、内切与内含；
 * 2. 使用圆心连线和公共弦半高恢复真实交点；
 * 3. 同时返回交点在两个圆上的角参数；
 * 4. 对所有长度先做特征尺度归一化，避免固定世界容差改变小尺度圆的拓扑关系。
 *
 * 完全重合的两个圆有无穷多交点，不能形成有限裁剪边界，因此返回空列表。
 */
export class CircleCircleIntersectionSolver extends AbstractPeriodicIntersectionSolver {
    /** 归一化几何关系判定容差。 */
    private static readonly RELATIVE_TOLERANCE = 1e-12;

    /**
     * 求解两个二维圆的全部有限真实交点。
     *
     * @param firstCircle 第一个圆定义。
     * @param secondCircle 第二个圆定义。
     * @returns 按第一个圆角参数升序排列的真实交点。
     */
    public solve(
        firstCircle: CircleIntersectionDefinition2D,
        secondCircle: CircleIntersectionDefinition2D,
    ): CircleCircleIntersection2D[] {
        if (!this.hasValidDefinition(firstCircle) || !this.hasValidDefinition(secondCircle)) {
            console.error('[CircleCircleIntersectionSolver] 圆定义无效，无法计算真实交点。', {
                firstCircle,
                secondCircle,
            });
            return [];
        }

        const centerOffset = secondCircle.center.clone().sub(firstCircle.center);
        const centerDistance = centerOffset.length();
        const geometryScale = Math.max(firstCircle.radius, secondCircle.radius, centerDistance);
        const normalizedDistance = centerDistance / geometryScale;
        const normalizedFirstRadius = firstCircle.radius / geometryScale;
        const normalizedSecondRadius = secondCircle.radius / geometryScale;
        const normalizedRadiusSum = normalizedFirstRadius + normalizedSecondRadius;
        const normalizedRadiusDifference = Math.abs(normalizedFirstRadius - normalizedSecondRadius);
        const tolerance = CircleCircleIntersectionSolver.RELATIVE_TOLERANCE;

        // 同心圆要么完全重合，要么没有交点；两种情况都不产生有限边界点。
        if (normalizedDistance <= tolerance) {
            return [];
        }

        // 圆心距大于半径和时两圆外离。
        if (normalizedDistance - normalizedRadiusSum > tolerance) {
            return [];
        }

        const centerAxis = centerOffset.clone().multiplyScalar(1 / centerDistance);

        // 外切点位于第一个圆沿圆心轴正向的半径终点。
        if (Math.abs(normalizedDistance - normalizedRadiusSum) <= tolerance) {
            const tangentPoint = firstCircle.center.clone().addScaledVector(centerAxis, firstCircle.radius);
            return [this.createIntersection(firstCircle, secondCircle, tangentPoint)];
        }

        // 两圆有两个交点时，先求公共弦中点沿圆心轴的距离，再求半弦高。
        if (
            normalizedRadiusSum - normalizedDistance > tolerance
            && normalizedDistance - normalizedRadiusDifference > tolerance
        ) {
            let normalizedBaseDistance = (
                normalizedDistance * normalizedDistance
                + normalizedFirstRadius * normalizedFirstRadius
                - normalizedSecondRadius * normalizedSecondRadius
            ) / (2 * normalizedDistance);
            const normalizedHeightSquared = normalizedFirstRadius * normalizedFirstRadius
                - normalizedBaseDistance * normalizedBaseDistance;

            // 边界附近的浮点误差可能使半弦高平方出现极小负值，将基距夹回圆周。
            if (normalizedHeightSquared < 0) {
                normalizedBaseDistance = Math.sign(normalizedBaseDistance) * normalizedFirstRadius;
            }
            const normalizedHeight = Math.sqrt(Math.max(
                0,
                normalizedFirstRadius * normalizedFirstRadius
                    - normalizedBaseDistance * normalizedBaseDistance,
            ));
            const basePoint = firstCircle.center.clone().addScaledVector(
                centerAxis,
                normalizedBaseDistance * geometryScale,
            );
            const perpendicularAxis = new THREE.Vector2(-centerAxis.y, centerAxis.x);
            const height = normalizedHeight * geometryScale;
            const firstPoint = basePoint.clone().addScaledVector(perpendicularAxis, height);
            const secondPoint = basePoint.clone().addScaledVector(perpendicularAxis, -height);

            return [
                this.createIntersection(firstCircle, secondCircle, firstPoint),
                this.createIntersection(firstCircle, secondCircle, secondPoint),
            ].sort((left, right) => left.firstCircleAngle - right.firstCircleAngle);
        }

        // 内切时，若第一个圆较小，切点位于圆心轴反向；否则位于正向。
        if (Math.abs(normalizedDistance - normalizedRadiusDifference) <= tolerance) {
            const directionSign = firstCircle.radius < secondCircle.radius ? -1 : 1;
            const tangentPoint = firstCircle.center.clone().addScaledVector(
                centerAxis,
                directionSign * firstCircle.radius,
            );
            return [this.createIntersection(firstCircle, secondCircle, tangentPoint)];
        }

        // 剩余情况为一圆严格内含于另一圆，没有交点。
        return [];
    }

    /**
     * 判断圆定义是否可参与解析求交。
     *
     * @param circle 圆定义。
     * @returns 圆心有限且半径为正时返回 true。
     */
    private hasValidDefinition(circle: CircleIntersectionDefinition2D): boolean {
        return Number.isFinite(circle.center.x)
            && Number.isFinite(circle.center.y)
            && Number.isFinite(circle.radius)
            && circle.radius > 0;
    }

    /**
     * 根据真实交点构造双圆参数结果。
     *
     * @param firstCircle 第一个圆。
     * @param secondCircle 第二个圆。
     * @param point 真实交点。
     * @returns 交点与双方角参数。
     */
    private createIntersection(
        firstCircle: CircleIntersectionDefinition2D,
        secondCircle: CircleIntersectionDefinition2D,
        point: THREE.Vector2,
    ): CircleCircleIntersection2D {
        return {
            point,
            firstCircleAngle: this.normalizeAngle(Math.atan2(
                point.y - firstCircle.center.y,
                point.x - firstCircle.center.x,
            )),
            secondCircleAngle: this.normalizeAngle(Math.atan2(
                point.y - secondCircle.center.y,
                point.x - secondCircle.center.x,
            )),
        };
    }

}
